import * as React from 'react'
import clx from 'classnames'
import { FieldsetSelect, type Option } from '../../components/FieldsetSelect'
import { DaysType } from '@/app/constants/configuration/daysType'

const defaultDisplayRelativeValues = [
  DaysType.Day1,
  DaysType.Day2,
  DaysType.Day3,
  DaysType.Day4,
  DaysType.Day5,
  DaysType.Day6,
  DaysType.Day7,
  DaysType.Day8,
  DaysType.Day9,
  DaysType.Day10,
  DaysType.Day11,
  DaysType.Day12,
  DaysType.Day13,
  DaysType.Day14,
  DaysType.Day15,
  DaysType.Day16,
  DaysType.Day17,
  DaysType.Day18,
  DaysType.Day19,
  DaysType.Day20,
  DaysType.Day21,
  DaysType.Day22,
  DaysType.Day23,
  DaysType.Day24,
  DaysType.Day25,
  DaysType.Day26,
  DaysType.Day27,
  DaysType.Day28,
  DaysType.Day29,
  DaysType.Day30,
  DaysType.LastDay,
]

type Props = {
  className?: string
  label?: string

  // main select
  options: Option[]
  selected: string
  handleSelect: (option: Option) => void
  placeholder?: string
  disabled?: boolean
  error?: string | boolean
  required?: boolean

  // days mode (number)
  relativeDaysSelected: number | null | undefined
  relativeDaysHandleSelect: (value: number | null) => void
  relativeDaysDisabled?: boolean

  // months mode (number)
  relativeMonthSelected: number | null | undefined
  relativeMonthHandleSelect: (value: number | null) => void
  relativeMonthDisabled?: boolean

  // months mode (month type select)
  monthTypeOptions: Option[]
  monthTypeSelected: string | number | null | undefined
  monthTypeHandleSelect: (option: Option) => void
  monthTypeDisabled?: boolean

  // determines mode
  displayRelativeValues?: Array<string | number>
}

export const RelativeDaysOrMonthsFields = ({
  className,
  label,

  options,
  selected,
  handleSelect,
  placeholder,
  disabled,
  error,
  required,

  relativeDaysSelected,
  relativeDaysHandleSelect,
  relativeDaysDisabled,

  relativeMonthSelected,
  relativeMonthHandleSelect,
  relativeMonthDisabled,

  monthTypeOptions,
  monthTypeSelected,
  monthTypeHandleSelect,
  monthTypeDisabled,

  displayRelativeValues = defaultDisplayRelativeValues,
}: Props) => {
  const isMonthsMode = React.useMemo(() => {
    // selected is string in your usage; displayRelativeValues might be enum values or strings
    return displayRelativeValues.some((v) => String(v) === String(selected))
  }, [displayRelativeValues, selected])

  const onNumberChange =
    (
      cb: (v: number | null) => void,
    ): React.ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      const raw = e.target.value
      if (raw === '') return cb(null)
      const parsed = Number(raw)
      cb(Number.isFinite(parsed) ? parsed : null)
    }

  return (
    <div
      className={clx('tw-gap-3', className)}
    >
      {label && (
        <label className="tw-group tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}

      <div className="tw-align-center tw-items-center tw-flex tw-justify-start tw-gap-2">
        {/* 1) main select */}
        <div className="tw-w-[260px]">
          <FieldsetSelect
            caretSize="xs"
            placeholder={placeholder}
            options={options}
            value={selected}
            handleChange={(_, meta) => handleSelect(meta.option)}
            disabled={disabled}
            error={error}
            required={required}
          />
        </div>

        <span>+</span>

        {/* 2/3) conditional */}
        {isMonthsMode ? (
          <>
            {/* months number */}
            <div className="tw-w-[80px]">
              <input
                className={clx(
                  'tw-border-content-border tw-w-full tw-rounded-md tw-border tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm',
                  (disabled || relativeMonthDisabled) && 'tw-opacity-60',
                )}
                type="number"
                value={relativeMonthSelected ?? ''}
                onChange={onNumberChange(relativeMonthHandleSelect)}
                disabled={disabled || relativeMonthDisabled}
              />
            </div>

            <span>miesiąc</span>

            {/* month type select */}
            <div className="tw-w-[240px]">
              <FieldsetSelect
                caretSize="xs"
                options={monthTypeOptions}
                value={monthTypeSelected ?? ''}
                handleChange={(_, meta) => monthTypeHandleSelect(meta.option)}
                disabled={disabled || monthTypeDisabled}
              />
            </div>
          </>
        ) : (
          <>
            {/* days number */}
            <div className="tw-w-[80px]">
              <input
                className={clx(
                  'tw-border-content-border tw-w-full tw-rounded-md tw-border tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm',
                  (disabled || relativeDaysDisabled) && 'tw-opacity-60',
                )}
                type="number"
                value={relativeDaysSelected ?? ''}
                onChange={onNumberChange(relativeDaysHandleSelect)}
                disabled={disabled || relativeDaysDisabled}
              />
            </div>

            <span>dni</span>
          </>
        )}
      </div>
    </div>
  )
}
